<template>
    <div v-if="$auth.check('ROLE_ADMIN')">

        <v-row align="center" no-gutters>
            <v-col cols="12" sm="8">
                <h1>Netter v1.0</h1>
            </v-col>
            <v-col cols="12" sm="4" class="text-sm-right">
                <v-btn icon @click="load()" :disabled="pending > 0" title="Atualizar">
                    <v-icon>mdi-refresh</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <v-progress-linear v-if="pending > 0" indeterminate color="primary" class="mb-2"/>

        <v-alert v-if="errors.length > 0" dense text type="warning" class="mt-2">
            Falha ao carregar: {{ errors.join(", ") }}.
        </v-alert>

        <v-row class="mt-2">
            <v-col cols="12" md="4">
                <v-card to="/internet" hover>
                    <v-card-title>Contratos ativos</v-card-title>
                    <v-card-text class="text-h4 text--primary">
                        <span v-if="total_active !== null">{{ total_active }}</span>
                        <span v-else>—</span>
                    </v-card-text>
                    <v-card-subtitle>
                        <span v-if="total_contracts !== null">{{ total_contracts }} contratos no total</span>
                        <span v-else>&nbsp;</span>
                    </v-card-subtitle>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title>Contratos bloqueados</v-card-title>
                    <v-card-text class="text-h4 orange--text text--darken-2">
                        <span v-if="total_blocked !== null">{{ total_blocked }}</span>
                        <span v-else>—</span>
                    </v-card-text>
                    <v-card-subtitle>Administrador, cliente ou pagamento</v-card-subtitle>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-card to="/client" hover>
                    <v-card-title>Clientes</v-card-title>
                    <v-card-text class="text-h4 text--primary">
                        <span v-if="clients !== null">{{ clients }}</span>
                        <span v-else>—</span>
                    </v-card-text>
                    <v-card-subtitle>Cadastros na base</v-card-subtitle>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="4">
                <v-card :to="{path: '/internet/report', query: {r: 'instalacao'}}" hover>
                    <v-card-title>Instalações em {{ month_label }}</v-card-title>
                    <v-card-text class="text-h4 green--text text--darken-2">
                        <span v-if="setup_count !== null">{{ setup_count }}</span>
                        <span v-else>—</span>
                    </v-card-text>
                    <v-card-subtitle>Contratos de internet instalados no mês</v-card-subtitle>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-card :to="{path: '/internet/report', query: {r: 'cancelamento'}}" hover>
                    <v-card-title>Cancelamentos em {{ month_label }}</v-card-title>
                    <v-card-text class="text-h4 red--text text--darken-2">
                        <span v-if="cancel_count !== null">{{ cancel_count }}</span>
                        <span v-else>—</span>
                    </v-card-text>
                    <v-card-subtitle>Contratos de internet cancelados no mês</v-card-subtitle>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title>Saldo de {{ month_label }}</v-card-title>
                    <v-card-text class="text-h4" :class="balance_class">
                        <span v-if="balance_count !== null">{{ balance_count > 0 ? "+" : "" }}{{ balance_count }}</span>
                        <span v-else>—</span>
                    </v-card-text>
                    <v-card-subtitle>Instalações menos cancelamentos</v-card-subtitle>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="5">
                <v-card>
                    <v-card-title>Contratos por produto</v-card-title>
                    <v-card-text>
                        <bar-chart v-if="data_product" :chart-data="data_product" :options="chart_options" :styles="chart_styles"/>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="7">
                <v-card>
                    <v-card-title>Contratos por status</v-card-title>
                    <v-card-text>
                        <bar-chart v-if="data_status" :chart-data="data_status" :options="chart_options_group" :styles="chart_styles"/>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>Resumo por produto</v-card-title>
                    <v-card-text>
                        <v-simple-table>
                            <thead>
                                <tr>
                                    <th>Produto</th>
                                    <th>Ativos</th>
                                    <th>Bloqueados</th>
                                    <th>Total</th>
                                    <th>Participação</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="product in products" :key="product.ref">
                                    <td>{{ product.name }}</td>
                                    <td>{{ active(product.ref) !== null ? active(product.ref) : "—" }}</td>
                                    <td>{{ blocked(product.ref) !== null ? blocked(product.ref) : "—" }}</td>
                                    <td>{{ count(product.ref) !== null ? count(product.ref) : "—" }}</td>
                                    <td>{{ share(product.ref) }}</td>
                                    <td>
                                        <v-btn icon :to="product.route" title="Relatórios">
                                            <v-icon>mdi-chart-bar</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

    </div>
</template>

<script>
import api from '../api/index.js';
import BarChart from '../components/BarChart.js';

const STATUS_LABELS = ["Habilitado", "Bloq. administrador", "Bloq. cliente", "Bloq. pagamento", "Cancelado"];

const BLOCKED_CODES = [1, 2, 3];

const MONTH_LABELS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const PRODUCTS = [
    {ref: "internet", name: "Internet", color: "#1976D2", route: "/internet/report"},
    {ref: "lastmile", name: "Lastmile", color: "#4CAF50", route: "/lastmile/report"},
    {ref: "lan2lan", name: "Lan2Lan", color: "#FB8C00", route: "/lan2lan/report"}
];

export default {
    components: { BarChart },
    data: () => ({
        products: PRODUCTS,
        status: {internet: null, lastmile: null, lan2lan: null},
        clients: null,
        setups: null,
        cancels: null,
        year: null,
        month: null,
        pending: 0,
        errors: [],
        chart_options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            scales: {
                yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }]
            }
        },
        chart_options_group: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: true, position: "bottom" },
            scales: {
                yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }]
            }
        },
        chart_styles: {
            position: "relative",
            height: "320px",
            width: "100%"
        }
    }),
    created: function() {
        var now = new Date();
        this.year = now.getFullYear();
        this.month = now.getMonth()+1;
        this.load();
    },
    computed: {
        month_label: function() {
            return MONTH_LABELS[this.month-1] + "/" + this.year;
        },
        total_active: function() {
            var me = this;
            return me.sum(me.products.map(function(p){ return me.active(p.ref); }));
        },
        total_blocked: function() {
            var me = this;
            return me.sum(me.products.map(function(p){ return me.blocked(p.ref); }));
        },
        total_contracts: function() {
            var me = this;
            return me.sum(me.products.map(function(p){ return me.count(p.ref); }));
        },
        setup_count: function() {
            return this.setups ? this.setups.length : null;
        },
        cancel_count: function() {
            return this.cancels ? this.cancels.length : null;
        },
        balance_count: function() {
            if(this.setup_count === null || this.cancel_count === null){
                return null;
            }
            return this.setup_count - this.cancel_count;
        },
        balance_class: function() {
            if(this.balance_count === null || this.balance_count == 0){
                return "text--primary";
            }
            return this.balance_count > 0 ? "green--text text--darken-2" : "red--text text--darken-2";
        },
        data_product: function() {
            var me = this;
            var loaded = me.products.filter(function(p){ return me.status[p.ref] !== null; });
            if(loaded.length == 0){
                return null;
            }
            return {
                labels: loaded.map(function(p){ return p.name; }),
                datasets: [{
                    label: "Contratos",
                    backgroundColor: loaded.map(function(p){ return p.color; }),
                    data: loaded.map(function(p){ return me.count(p.ref); })
                }]
            };
        },
        data_status: function() {
            var me = this;
            var loaded = me.products.filter(function(p){ return me.status[p.ref] !== null; });
            if(loaded.length == 0){
                return null;
            }
            var codes = [];
            loaded.forEach(function(p){
                me.status[p.ref].forEach(function(row){
                    var code = Number(row[0]);
                    if(codes.indexOf(code) < 0){
                        codes.push(code);
                    }
                });
            });
            codes.sort(function(a, b){ return a - b; });
            return {
                labels: codes.map(function(code){ return STATUS_LABELS[code] || "Desconhecido"; }),
                datasets: loaded.map(function(p){
                    return {
                        label: p.name,
                        backgroundColor: p.color,
                        data: codes.map(function(code){ return me.statusCount(p.ref, code); })
                    };
                })
            };
        }
    },
    methods: {
        headers: function(){
            return { headers: { Authorization: "Bearer " + localStorage.getItem("token") } };
        },
        begin: function(){
            this.pending++;
        },
        done: function(){
            this.pending--;
        },
        fail: function(label, error){
            this.done();
            console.log(error);
            if(this.errors.indexOf(label) < 0){
                this.errors.push(label);
            }
        },
        sum: function(values){
            var loaded = values.filter(function(v){ return v !== null && v !== undefined; });
            if(loaded.length == 0){
                return null;
            }
            return loaded.reduce(function(total, v){ return total + Number(v); }, 0);
        },
        statusCount: function(ref, code){
            var rows = this.status[ref];
            if(!rows){
                return 0;
            }
            var found = rows.filter(function(row){ return Number(row[0]) == code; })[0];
            return found ? Number(found[1]) : 0;
        },
        active: function(ref){
            return this.status[ref] ? this.statusCount(ref, 0) : null;
        },
        blocked: function(ref){
            var me = this;
            if(!me.status[ref]){
                return null;
            }
            return BLOCKED_CODES.reduce(function(total, code){
                return total + me.statusCount(ref, code);
            }, 0);
        },
        count: function(ref){
            var rows = this.status[ref];
            if(!rows){
                return null;
            }
            return rows.reduce(function(total, row){ return total + Number(row[1] || 0); }, 0);
        },
        share: function(ref){
            var count = this.count(ref);
            if(count === null || !this.total_contracts){
                return "—";
            }
            return (count / this.total_contracts * 100).toFixed(1).replace(".", ",") + "%";
        },
        load: function(){
            var me = this;
            me.errors = [];
            me.products.forEach(function(product){
                me.loadStatus(product);
            });
            me.loadClients();
            me.loadSetups();
            me.loadCancels();
        },
        loadStatus: function(product){
            var me = this;
            me.begin();
            api.get("/"+product.ref+"/report/status", me.headers())
            .then(function(response){
                me.done();
                me.status[product.ref] = response.data;
            })
            .catch(function(error){
                me.fail("status "+product.name, error);
            });
        },
        loadClients: function(){
            var me = this;
            me.begin();
            api.get("/client/count", me.headers())
            .then(function(response){
                me.done();
                me.clients = response.data;
            })
            .catch(function(error){
                me.fail("clientes", error);
            });
        },
        loadSetups: function(){
            var me = this;
            me.begin();
            api.get("/internet/instalacoes/"+me.year+"/"+me.month, me.headers())
            .then(function(response){
                me.done();
                me.setups = response.data;
            })
            .catch(function(error){
                me.fail("instalações", error);
            });
        },
        loadCancels: function(){
            var me = this;
            me.begin();
            api.get("/internet/cancelados/"+me.year+"/"+me.month, me.headers())
            .then(function(response){
                me.done();
                me.cancels = response.data;
            })
            .catch(function(error){
                me.fail("cancelamentos", error);
            });
        }
    }
};
</script>
